import React, { useState, useCallback, useRef } from 'react';

/**
 * Props for the MediaUpload component.
 */
interface MediaUploadProps {
    /** URL to upload media to (e.g., `/hex-media/1` or `/character/1/media`) */
    uploadUrl: string;
    /** Optional page ID to associate media with */
    pageId?: number;
    /** Callback when upload completes successfully */
    onUploadComplete: (media: UploadedMedia) => void;
    /** Callback when upload fails */
    onUploadError?: (error: string) => void;
}

/**
 * Represents an uploaded media item returned from the server.
 */
export interface UploadedMedia {
    id: number;
    original_filename: string;
    mime_type: string;
    file_size: number;
    formatted_size: string;
    width: number | null;
    height: number | null;
    alt_text: string | null;
    urls: {
        small: string;
        medium: string;
        large: string;
    };
    created_at: string;
}

/**
 * MediaUpload component provides a drag-and-drop area for uploading images.
 * Displays upload progress and handles file validation.
 */
export function MediaUpload({
    uploadUrl,
    pageId,
    onUploadComplete,
    onUploadError,
}: MediaUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Allowed file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSizeMB = 10;

    /**
     * Validate file before upload.
     */
    const validateFile = (file: File): string | null => {
        if (!allowedTypes.includes(file.type)) {
            return `Invalid file type. Allowed: JPG, PNG, GIF, WebP`;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
            return `File too large. Maximum size: ${maxSizeMB}MB`;
        }
        return null;
    };

    /**
     * Upload a file to the server.
     */
    const uploadFile = async (file: File) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            onUploadError?.(validationError);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            if (pageId) {
                formData.append('page_id', pageId.toString());
            }

            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            const xhr = new XMLHttpRequest();

            // Track upload progress
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percent);
                }
            });

            // Handle completion
            xhr.addEventListener('load', () => {
                setIsUploading(false);
                if (xhr.status >= 200 && xhr.status < 300) {
                    const response = JSON.parse(xhr.responseText);
                    onUploadComplete(response.media);
                } else {
                    const errorMsg = xhr.responseText
                        ? JSON.parse(xhr.responseText).message || 'Upload failed'
                        : 'Upload failed';
                    setError(errorMsg);
                    onUploadError?.(errorMsg);
                }
            });

            // Handle errors
            xhr.addEventListener('error', () => {
                setIsUploading(false);
                const errorMsg = 'Network error during upload';
                setError(errorMsg);
                onUploadError?.(errorMsg);
            });

            xhr.open('POST', uploadUrl);
            xhr.setRequestHeader('Accept', 'application/json');
            if (csrfToken) {
                xhr.setRequestHeader('X-XSRF-TOKEN', decodeURIComponent(csrfToken));
            }
            xhr.send(formData);
        } catch (err) {
            setIsUploading(false);
            const errorMsg = err instanceof Error ? err.message : 'Upload failed';
            setError(errorMsg);
            onUploadError?.(errorMsg);
        }
    };

    // Drag event handlers
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            uploadFile(files[0]);
        }
    }, [uploadUrl, pageId]);

    // Click to select file
    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            uploadFile(files[0]);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    return (
        <div className="space-y-2">
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={allowedTypes.join(',')}
                onChange={handleFileChange}
                className="hidden"
            />

            {/* Drop zone */}
            <div
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`
                    relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                    transition-colors duration-200
                    ${isDragging
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }
                    ${isUploading ? 'pointer-events-none opacity-75' : ''}
                `}
            >
                {isUploading ? (
                    <div className="space-y-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Uploading... {uploadProgress}%
                        </div>
                        {/* Progress bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {/* Upload icon */}
                        <svg
                            className="mx-auto h-10 w-10 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                        >
                            <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium text-blue-600 dark:text-blue-400">
                                Click to upload
                            </span>{' '}
                            or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                            PNG, JPG, GIF, WebP up to {maxSizeMB}MB
                        </p>
                    </div>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </div>
            )}
        </div>
    );
}

export default MediaUpload;

